import { MinusOutlined } from '@ant-design/icons';
import { useFormItemEnabled } from '@framework/form/item/useFormItemEnabled.ts';
import { t } from '@framework/i18n/intl/i18next.ts';
import { ButtonDeleteConfirm } from '@framework/ui/components/button/ButtonDeleteConfirm.tsx';
import { Button, Tooltip } from 'antd';
import { useCallback, useMemo } from 'react';
import { type IRepeatableWidget } from '../IRepeatableWidget.ts';
import { AlignItemWrapper } from '../style/RepeatableContainer.tsx';

export interface IRemoveRowButtonProps {
  widget: IRepeatableWidget;
  index: number;
  onRemove: (index: number) => void;
  itemCount: number;
}

/**
 * Bottone per l'eliminazione di una specifica riga in un repeatable widget.
 */
export function RemoveRowButton(props: IRemoveRowButtonProps) {
  const { widget, index, onRemove, itemCount } = props;

  const repeatRule = widget.rules?.repeatableRange;
  const isMin = useMemo(
    () => repeatRule?.min && itemCount <= repeatRule.min,
    [itemCount, repeatRule],
  );

  const { visuallyDisabled } = useFormItemEnabled();

  const handleOnClick = useCallback(() => {
    if (isMin) return;
    if (visuallyDisabled) return;
    onRemove(index);
  }, [visuallyDisabled, index, isMin, onRemove]);

  return (
    <AlignItemWrapper>
      <Tooltip
        title={
          //  Se è disabilitato non mostro il tooltip
          !visuallyDisabled && isMin
            ? t(
                'widgets.remove-row-buttton.min-options-reached',
                'Numero minimo di opzioni raggiunto',
              )
            : undefined
        }
      >
        {widget.layout?.showConfirmDelete ? (
          <ButtonDeleteConfirm
            size="large"
            disabled={visuallyDisabled || itemCount <= (repeatRule?.min ?? 0)}
            buttonIcon={<MinusOutlined aria-label="Rimuovi" />}
            title={widget.layout?.confirmDeleteTitle}
            okText={widget.layout?.confirmDeleteButtonLabel}
            cancelText={widget.layout?.cancelDeleteButtonLabel}
            iconOnlyButton
            // buttonDanger
            buttonType="default"
            onConfirm={handleOnClick}
          />
        ) : (
          <Button
            aria-label="Rimuovi"
            size="large"
            disabled={visuallyDisabled || itemCount <= (repeatRule?.min ?? 0)}
            icon={<MinusOutlined aria-hidden />}
            onClick={handleOnClick}
          />
        )}
      </Tooltip>
    </AlignItemWrapper>
  );
}
